import { useEffect, useMemo, useRef } from "react";

import { CardComponent } from "@chargebee/chargebee-js-react-wrapper";
import type ChargebeeComponents from "@chargebee/chargebee-js-react-wrapper/dist/components/ComponentGroup";
import type { Styles } from "@chargebee/chargebee-js-types";
import { TextField } from "@shapr3d/frontend-lib";
import classNames from "classnames";
import { useController, useFormContext } from "react-hook-form";
import { useIntl } from "react-intl";

import usePrevious from "../../hooks/usePrevious";
import useTranslation from "../../hooks/useTranslation";
import { PaymentType, useChargebee } from "../../lib/ChargebeeAdapter";
import type { CheckoutFormData } from "../ChargebeeCheckout/ChargebeeCheckoutSchema";
import type { CreditCardInputChangeEvent, CreditCardInputProps } from "./CreditCardInputTypes";

import style from "./CreditCardInput.module.css";

const cardStyles = {
  base: {
    color: "#3C3C3C",
    fontFamily: "Quicksand, Open Sans, Segoe UI, sans-serif",
    fontSize: "16px",
    fontSmoothing: "antialiased",

    ":focus": {
      color: "#3C3C3C",
    },

    "::placeholder": {
      color: "var(--text-tertiary-label)",
    },

    ":focus::placeholder": {
      color: "var(--text-tertiary-label)",
    },
  },
  invalid: {
    color: "#CC2929",
    ":focus": {
      color: "#CC2929",
    },
    "::placeholder": {
      color: "#CC2929",
    },
  },
  empty: {},
} satisfies Styles;

const CreditCardInput = ({ control, name, onValidationErrors }: CreditCardInputProps) => {
  const cardRef = useRef<ChargebeeComponents>(null);
  const { isLoading } = useChargebee();
  const {
    field,
    fieldState: { error },
  } = useController({
    name,
    control,
  });
  const { trigger, watch } = useFormContext<CheckoutFormData>();
  const { t, m } = useTranslation();
  const { locale } = useIntl();
  const isoLocale = useMemo(() => locale.split("-")[0], [locale]);

  const translatedErrorMessage = useMemo(() => {
    if (!error?.message) return null;
    const errorCode = field.value?.error?.errorCode;
    switch (errorCode) {
      case "card_number_incomplete":
        return t(m.Checkout.ValidationError.IncompleteCardNumber);
      case "card_number_invalid":
        return t(m.Checkout.ValidationError.InvalidCardNumber);
      case "card_cvv_invalid":
        return t(m.Checkout.ValidationError.InvalidCvv);
      default:
        if (!field.value) {
          return t(m.Checkout.ValidationError.Required);
        }
        return error.message;
    }
  }, [error?.message, field.value, t, m]);

  const paymentType = watch("paymentType");
  const previousPaymentType = usePrevious(paymentType);
  useEffect(() => {
    if (
      paymentType === PaymentType.Card &&
      previousPaymentType &&
      previousPaymentType !== paymentType
    ) {
      trigger(name);
    }
  }, [trigger, name, paymentType, previousPaymentType]);

  useEffect(() => {
    if (error && Object.keys(error).length > 0) {
      onValidationErrors?.(error);
    }
  }, [error, onValidationErrors]);

  if (isLoading) {
    return <TextField disabled={true} />;
  }

  return (
    <div>
      <div className={classNames(style.input, error && style.error)}>
        <CardComponent
          styles={cardStyles}
          ref={cardRef}
          locale={isoLocale}
          onChange={(e: CreditCardInputChangeEvent) => {
            if (e.empty) {
              field.onChange(null);
            } else {
              field.onChange({
                ...e,
                API: {
                  authorizeWith3ds: cardRef.current?.authorizeWith3ds.bind(cardRef.current),
                },
              });
            }
            trigger(name);
          }}
        />
      </div>
      <div className={style.errorMessage}>{translatedErrorMessage}</div>
    </div>
  );
};

CreditCardInput.displayName = "CreditCardInput";
export default CreditCardInput;
